import React from 'react';
import { useTheme } from 'styled-components';

import useBreakpoint from 'Common/hooks/useBreakpoint';

import { FiftyTwoWeeksCanvasProps, MappedBlock, isJSXElement } from 'Common/util/cmsCanvas';

import WeekCalendar, { WeekCalendarProps } from 'Elements/WeekCalendar';
import Image, { ImageProps } from 'Elements/Image';
import { H1, H2 } from 'Elements/Typography';
import Section from 'Elements/Section';
import Main from 'Elements/Main';

import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import FiftyTwoWeeks, { FiftyTwoWeeksCardProps } from 'Components/Card/FiftyTwoWeeks';
import Video, { VideoProps } from 'Components/Video';
import SupportBlock from 'Components/SupportBlock';
import LineList from 'Components/List/LineList';
import Accordion from 'Components/Accordion';
import Grid, { Col } from 'Components/Grid';

import { PromoPointSocialsProps } from 'Modules/PromoPoint/Socials';

import { DefaultGroupWrapper, WeekWrapper, Promopoint, PromoPointWrapper } from './Article.styled';
import FlexBox from 'Elements/FlexBox';

export interface Props {
    heading: string;
    breadcrumbs: BreadcrumbsProps;
    canvasContent?: MappedBlock<FiftyTwoWeeksCanvasProps>[];
    signUpPromo: PromoPointSocialsProps;
    week: WeekCalendarProps;
    summary: string;
    video?: VideoProps;
    image: ImageProps;
    prevCard?: FiftyTwoWeeksCardProps;
    nextCard?: FiftyTwoWeeksCardProps;
}

const Article: React.FC<Props> = props => {
    const { colors, breakpoints } = useTheme();
    const isMobileOnly = !useBreakpoint(breakpoints.medium.value);
    const {
        breadcrumbs,
        canvasContent,
        signUpPromo,
        heading,
        week,
        summary,
        video,
        image,
        prevCard,
        nextCard
    } = props;

    const renderContent = (
        item: MappedBlock<FiftyTwoWeeksCanvasProps>,
        index: number
    ): JSX.Element | undefined => {
        const ComposerItemWrapper: React.FC<{
            removeMargin?: boolean;
        }> = ({ children, removeMargin }) => {
            return (
                <Grid
                    style={{
                        marginTop: removeMargin ? '0' : '1rem',
                        marginBottom: removeMargin ? '0rem' : '2rem'
                    }}
                >
                    <Col l={[3, 11]}>
                        <div>{children}</div>
                    </Col>
                </Grid>
            );
        };

        if (item.groupName === '_defaultGroup' && isJSXElement(item.elements)) {
            return (
                <ComposerItemWrapper>
                    <DefaultGroupWrapper>
                        {item.elements.map(element => (
                            <React.Fragment key={element.key}>{element}</React.Fragment>
                        ))}
                    </DefaultGroupWrapper>
                </ComposerItemWrapper>
            );
        }

        if (!isJSXElement(item.elements)) {
            const component = item.elements;

            return (
                <>
                    {component.accordionCollectionCanvas && (
                        <ComposerItemWrapper key={index} removeMargin>
                            {component.accordionCollectionCanvas.map(({ children, ...rest }) => (
                                <Accordion {...rest} key={rest.id}>
                                    {children}
                                </Accordion>
                            ))}
                        </ComposerItemWrapper>
                    )}
                    {component.list && (
                        <ComposerItemWrapper key={index}>
                            <SupportBlock
                                heading={
                                    <H2
                                        weight={'bold'}
                                        size={{ base: '1.5rem', large: '1.75rem' }}
                                        spacing={{ base: { margin: 0 } }}
                                    >
                                        {component.list.heading}
                                    </H2>
                                }
                                backgroundColor={colors.secondary.tints[1].hex}
                            >
                                <LineList
                                    items={component.list.items}
                                    borderColor={colors.secondary.tints[3].hex}
                                />
                            </SupportBlock>
                        </ComposerItemWrapper>
                    )}
                    {component.video && (
                        <ComposerItemWrapper key={index}>
                            <Video {...component.video.embed} />
                        </ComposerItemWrapper>
                    )}
                </>
            );
        }
    };

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <Section
                id="introduction"
                paddingTop={2.75}
                background
                backgroundStart
                backgroundTop="-24rem"
                gradient
                shape="box"
                paddingBottom={0}
            >
                <Grid>
                    <Col m={[2, 12]} l={[4, 10]}>
                        <FlexBox alignItems="center">
                            <WeekWrapper>
                                <WeekCalendar {...week} />
                            </WeekWrapper>
                            <H1
                                weight={'bold'}
                                spacing={{ large: { margin: '1.25rem 0 1.5rem' } }}
                                size={{ base: '1.5rem', large: '2rem' }}
                                lineHeight={1.1}
                            >
                                {heading}
                            </H1>
                        </FlexBox>
                        <p>{summary}</p>
                        {video ? (
                            <Video {...video} />
                        ) : (
                            <Image
                                {...image}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '0.5rem'
                                }}
                            />
                        )}
                    </Col>
                </Grid>
            </Section>
            {canvasContent && (
                <Section id="story-canvas" paddingTop={0.5}>
                    <React.Fragment>
                        {canvasContent.map((item, index) => {
                            const content = renderContent(item, index);
                            return <React.Fragment key={index}>{content}</React.Fragment>;
                        })}
                    </React.Fragment>
                </Section>
            )}

            <Section
                id="promo-weeks"
                paddingTop={1.5}
                paddingBottom={1.5}
                background
                backgroundColor={colors.secondary.tints[1].hex}
            >
                <Grid rowGap="1.5rem">
                    {prevCard?.image && (
                        <Col m={3}>
                            <H2
                                weight={'bold'}
                                size={{ base: '1.5rem', large: '1.625rem' }}
                                spacing={{
                                    base: { margin: '0 0 0.75rem' }
                                }}
                                lineHeight={1.45}
                            >
                                Previous week
                            </H2>
                            <FiftyTwoWeeks {...prevCard} />
                        </Col>
                    )}
                    <Col
                        m={
                            prevCard?.image === undefined
                                ? [1, 10]
                                : nextCard?.image === undefined
                                ? [4, 13]
                                : [4, 10]
                        }
                        order={isMobileOnly ? 3 : undefined}
                    >
                        <PromoPointWrapper>
                            <Promopoint
                                {...signUpPromo}
                                isTabletHalfWidthCard={
                                    prevCard?.image && nextCard?.image ? true : false
                                }
                                heading={
                                    <H2
                                        size={{ base: '1.375rem', large: '1.75rem' }}
                                        spacing={{
                                            base: { margin: '0 0 0.8125rem' },
                                            large: { margin: '0.15rem 0 1rem' }
                                        }}
                                    >
                                        {signUpPromo.heading}
                                    </H2>
                                }
                            />
                        </PromoPointWrapper>
                    </Col>
                    {nextCard?.image && (
                        <Col m={3}>
                            <H2
                                weight={'bold'}
                                size={{ base: '1.5rem', large: '1.625rem' }}
                                spacing={{
                                    base: { margin: '0 0 0.75rem' }
                                }}
                                lineHeight={1.45}
                            >
                                Upcoming week
                            </H2>
                            <FiftyTwoWeeks {...nextCard} />
                        </Col>
                    )}
                </Grid>
            </Section>
        </Main>
    );
};

export default Article;
